import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { SectionTitle, Empty, Spinner } from "@/components/common";
import { toast } from "sonner";
import { Plus, FileSearch, ArrowRight, Trophy, XCircle } from "lucide-react";

const STAGE = { upload: "Upload", understand: "Understand", qualify: "Qualify", review: "Review", convert: "Converted" };

export default function Opportunities() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", rfq_number: "" });
  const { data, isLoading } = useQuery({ queryKey: ["opportunities"], queryFn: async () => (await api.get("/opportunities")).data });

  const create = async () => {
    if (!form.title || !form.client_name) { toast.error("Title and client are required"); return; }
    const res = await api.post("/opportunities", form);
    setOpen(false);
    navigate(`/opportunities/${res.data.id}`);
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="rounded-2xl brand-gradient text-white p-8 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#29abe2]/20 blur-3xl" />
        <div className="relative z-10">
          <div className="text-[11px] font-semibold tracking-[0.2em] text-[#c9a227] uppercase">RFQ-to-Proposal Studio</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Win more bids</h1>
          <p className="text-white/70 mt-2 max-w-xl text-sm">Upload an RFQ, extract a clause-cited profile, qualify the bid, and generate branded Technical, Project and Commercial proposals — then convert a win into a delivery project.</p>
        </div>
      </div>

      <SectionTitle eyebrow="Pipeline" title="Opportunities" action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-opp-btn"><Plus size={15} /> New opportunity</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New opportunity</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Opportunity title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="S/4HANA implementation for …" data-testid="opp-title" /></div>
              <div><Label>Client / Issuer</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="opp-client" /></div>
              <div><Label>RFQ / Tender number</Label><Input value={form.rfq_number} onChange={(e) => setForm({ ...form, rfq_number: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="create-opp">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />

      {isLoading ? <Spinner /> : (data || []).length === 0 ? (
        <Empty title="No opportunities yet" hint="Create one and upload the RFQ to get started." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((o) => (
            <button key={o.id} onClick={() => navigate(`/opportunities/${o.id}`)} data-testid={`opp-card-${o.id}`} className="text-left rounded-xl border bg-card p-5 card-hover">
              <div className="flex items-center justify-between">
                <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#0f2557]/10 dark:bg-[#29abe2]/10 text-[#0f2557] dark:text-[#29abe2]"><FileSearch size={18} /></span>
                {o.status === "won" ? <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center gap-1"><Trophy size={12} /> Won</span>
                  : o.status === "lost" ? <span className="text-xs px-2 py-1 rounded-full bg-red-500/15 text-red-500 flex items-center gap-1"><XCircle size={12} /> Lost</span>
                  : <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">{STAGE[o.stage] || o.stage}</span>}
              </div>
              <div className="font-semibold text-foreground mt-3">{o.title}</div>
              <div className="text-sm text-muted-foreground">{o.client_name}{o.rfq_number ? ` · ${o.rfq_number}` : ""}</div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t text-xs text-muted-foreground">
                <span>{o.proposal_count || 0} proposals</span><ArrowRight size={14} className="text-[#29abe2]" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
